import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Lightning, ArrowRight, Users, ListChecks, Barbell, WarningOctagon, Sparkle, Trophy,
} from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSince = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { year: "numeric", month: "short" });
};

export default function CoachPublic() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/coach/public/${slug}`)
      .then(({ data }) => setCoach(data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error("Failed to load");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const apply = () => {
    if (!coach?.invite_code) {
      toast.error("Coach is not accepting new applications");
      return;
    }
    navigate(`/join/${coach.invite_code}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base text-white flex items-center justify-center">
        <div className="label-tiny text-text-secondary">LOADING...</div>
      </div>
    );
  }
  if (notFound || !coach) {
    return (
      <div className="min-h-screen bg-surface-base text-white flex items-center justify-center p-6" data-testid="coach-public-not-found">
        <div className="max-w-md text-center">
          <WarningOctagon size={40} weight="duotone" className="text-yellow-400 mx-auto mb-3" />
          <h1 className="font-heading text-2xl mb-2">COACH NOT FOUND</h1>
          <p className="text-sm text-text-secondary">
            The coach you&apos;re looking for doesn&apos;t exist or is no longer accepting a public profile.
          </p>
        </div>
      </div>
    );
  }

  const initials = (coach.name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-surface-base text-white" data-testid="coach-public-page">
      {/* Top bar */}
      <div className="border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-white hover:text-brand-accent transition-colors"
          data-testid="coach-public-home"
        >
          <img src="/icon-192.png" alt="Edge Training" className="h-7 w-7 rounded-md" />
          <span className="font-heading text-sm tracking-tight leading-none">EDGE TRAINING</span>
        </button>
        <button
          onClick={() => navigate("/login")}
          className="label-tiny text-text-secondary hover:text-white transition-colors"
          data-testid="coach-public-login"
        >
          LOG IN →
        </button>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div
            className="absolute -top-32 -right-32 w-96 h-96 bg-brand-accent/20 rounded-full blur-3xl"
            aria-hidden
          />
          <div
            className="absolute -bottom-32 -left-32 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl"
            aria-hidden
          />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-14 lg:py-20">
          <div className="label-tiny text-brand-accent mb-4">// COACH · EDGE TRAINING</div>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Avatar */}
            <div className="shrink-0">
              {coach.picture ? (
                <img
                  src={coach.picture}
                  alt={coach.name}
                  className="w-32 h-32 md:w-40 md:h-40 object-cover border-2 border-brand-accent"
                  data-testid="coach-public-avatar"
                />
              ) : (
                <div
                  className="w-32 h-32 md:w-40 md:h-40 flex items-center justify-center bg-brand-primary text-white font-heading text-4xl border-2 border-brand-accent"
                  data-testid="coach-public-avatar-initials"
                >
                  {initials}
                </div>
              )}
            </div>
            {/* Meta */}
            <div className="flex-1">
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-3 leading-tight inline-flex items-center gap-3 flex-wrap"
                data-testid="coach-public-name"
              >
                {coach.name?.toUpperCase()}
                <VerifiedBadge verified={coach.is_verified} size="md" showLabel />
              </h1>
              {coach.coach_specialty && (
                <div
                  className="inline-block border border-brand-accent text-brand-accent px-3 py-1 label-tiny tracking-widest mb-4"
                  data-testid="coach-public-specialty"
                >
                  {coach.coach_specialty.toUpperCase()}
                </div>
              )}
              {coach.coach_bio && (
                <p
                  className="text-lg text-text-primary/90 leading-relaxed max-w-2xl mb-6"
                  data-testid="coach-public-bio"
                >
                  {coach.coach_bio}
                </p>
              )}

              {/* CTA */}
              {coach.accepts_applications && coach.invite_code ? (
                <button
                  onClick={apply}
                  className="btn-kinetic px-8 py-4 text-sm label-tiny tracking-[0.25em] flex items-center gap-3"
                  data-testid="apply-to-coach-button"
                >
                  <Sparkle size={16} weight="fill" />
                  APPLY TO COACH WITH ME
                  <ArrowRight size={16} weight="bold" />
                </button>
              ) : (
                <div
                  className="inline-block border border-white/10 px-6 py-3 text-sm text-text-secondary label-tiny"
                  data-testid="coach-public-closed"
                >
                  NOT CURRENTLY ACCEPTING NEW ATHLETES
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 mb-12">
          {[
            { label: "ACTIVE ATHLETES", val: coach.active_clients, icon: Users, testid: "stat-clients" },
            { label: "PROGRAMS BUILT", val: coach.total_programs, icon: ListChecks, testid: "stat-programs" },
            { label: "COACHING SINCE", val: fmtSince(coach.coach_since) || "—", icon: Trophy, testid: "stat-since" },
          ].map((s) => (
            <div key={s.label} className="bg-surface-DEFAULT p-6" data-testid={s.testid}>
              <div className="flex items-center justify-between mb-3">
                <s.icon size={20} weight="duotone" className="text-brand-accent" />
                <span className="label-tiny">{s.label}</span>
              </div>
              <div className="font-heading text-3xl tabular-nums">{s.val}</div>
            </div>
          ))}
        </div>

        {/* Value props */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {[
            {
              title: "PERSONALISED PROGRAMS",
              copy: "Every training block is built for your goals, schedule and injury history — never a copy-paste template.",
            },
            {
              title: "IN-APP MESSAGING",
              copy: "Direct 1:1 chat with your coach. Ask questions, log check-ins, get accountability nudges.",
            },
            {
              title: "VIDEO FORM REVIEW",
              copy: "Send workout clips and get timestamped cues — technique corrections you can watch back anytime.",
            },
          ].map((v) => (
            <div key={v.title} className="tactical-card p-6">
              <div className="label-tiny text-brand-accent mb-2">// WHY WORK WITH ME</div>
              <h3 className="font-heading text-lg mb-2">{v.title}</h3>
              <p className="text-sm text-text-secondary leading-relaxed">{v.copy}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        {coach.accepts_applications && coach.invite_code && (
          <div
            className="tactical-card p-8 text-center border-brand-accent/30"
            data-testid="coach-public-bottom-cta"
          >
            <Barbell size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
            <h2 className="font-heading text-2xl mb-2">READY TO TRAIN WITH {coach.name?.toUpperCase().split(" ")[0]}?</h2>
            <p className="text-sm text-text-secondary mb-5 max-w-md mx-auto">
              Create a free Edge Training account and get connected in under a minute.
            </p>
            <button
              onClick={apply}
              className="btn-kinetic px-8 py-3 text-sm label-tiny tracking-[0.25em]"
              data-testid="apply-to-coach-bottom-button"
            >
              APPLY TO COACH WITH ME →
            </button>
          </div>
        )}

        <div className="mt-12 text-center label-tiny text-text-secondary/60">
          POWERED BY EDGE TRAINING — THE COACHING PLATFORM
        </div>
      </div>
    </div>
  );
}
